/**
 * Marketing homepage. Rendered by App.tsx auth gate when an unauthed
 * visitor lands on `/`. The current LoginPage continues to live at
 * `/login` — clicking START FREE / CREATE YOUR FIRST PROJECT below
 * does a top-level navigation there.
 *
 * Design: `design/01-editorial.html` (editorial constitution + warm-paper
 * brand tokens + amber accent). Styles live in `homepage.css` and are
 * scoped under `.hp-root` so they don't leak into the rest of the app.
 *
 * The footer "GitHub" / "Google" auth icons link straight to the OAuth
 * start endpoints (top-level <a> so the CSRF state cookie sticks);
 * "Magic Link" sends the user to /login where the email form lives.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from './LanguageToggle';
import './homepage.css';

type StaleState = 'idle' | 'rewriting' | 'done';

const ORIGINAL_DECISION = 'elemental (fire / water / earth / air)';
const NEW_DECISION = 'soul-based (grief / hope / wrath / wonder)';
const DEP_IDS: ['dep1', 'dep2', 'dep3'] = ['dep1', 'dep2', 'dep3'];

export function Homepage() {
  const { t } = useTranslation();
  const [staleState, setStaleState] = useState<StaleState>('idle');
  const [flash, setFlash] = useState(false);
  const [decisionText, setDecisionText] = useState<'original' | 'new'>('original');
  const [staleDeps, setStaleDeps] = useState<Set<string>>(new Set());
  const [narrate, setNarrate] = useState(t('homepage.demoNarrateIdle'));
  const timeoutsRef = useRef<number[]>([]);

  // Clear any in-flight timers if the user navigates away mid-animation.
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((id) => window.clearTimeout(id));
      timeoutsRef.current = [];
    };
  }, []);

  function scheduleTimeout(fn: () => void, ms: number) {
    const id = window.setTimeout(fn, ms);
    timeoutsRef.current.push(id);
  }

  function resetDemo() {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutsRef.current = [];
    setDecisionText('original');
    setFlash(false);
    setStaleDeps(new Set());
    setNarrate(t('homepage.demoNarrateIdle'));
    setStaleState('idle');
  }

  function runDemo() {
    if (staleState === 'done') {
      resetDemo();
      return;
    }
    if (staleState === 'rewriting') return;
    setStaleState('rewriting');
    setNarrate('> write_card(c-04, decision)');
    setFlash(true);

    scheduleTimeout(() => {
      setDecisionText('new');
      setNarrate('> substrate detects decision overwrite');
    }, 700);

    scheduleTimeout(() => {
      setNarrate('> walking →depends-on (3 transitive)');
    }, 1300);

    DEP_IDS.forEach((id, i) => {
      scheduleTimeout(() => {
        setStaleDeps((prev) => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });
        if (i === DEP_IDS.length - 1) {
          setNarrate('> 3 dependents flagged stale ✓');
          setStaleState('done');
        }
      }, 1900 + i * 450);
    });
  }

  const demoButtonLabel =
    staleState === 'idle'
      ? t('homepage.demoButtonIdle')
      : staleState === 'rewriting'
      ? t('homepage.demoButtonWriting')
      : t('homepage.demoButtonReset');

  return (
    <div className="hp-root">
      <div className="page paper-tex">
        {/* ── Header ── */}
        <header className="top">
          <div className="wrap">
            <a href="#top" className="brand">
              Draftlize
            </a>
            <span className="edition">{t('homepage.edition')}</span>
            <nav>
              <a href="#problem">{t('homepage.navProblem')}</a>
              <a href="#mechanic">{t('homepage.navMechanic')}</a>
              <a href="#personas">{t('homepage.navPeople')}</a>
              <a href="#docs">{t('homepage.navDocuments')}</a>
              <a href="#pricing">{t('homepage.navPricing')}</a>
            </nav>
            <LanguageToggle />
            <a href="/login" className="cta">
              {t('homepage.navEarlyAccess')}
            </a>
          </div>
        </header>

        {/* ── Hero ── */}
        <section id="top" className="hero">
          <div className="hero-grid">
            <div>
              <span className="label label-amber">{t('homepage.heroLabel')}</span>
              <h1 className="serif">
                {t('homepage.heroTitleLine1')}
                <br />
                <em>{t('homepage.heroTitleLine2')}</em>
              </h1>
              <p className="lede">
                {t('homepage.heroLedePart1')}{' '}
                <strong>{t('homepage.heroLedeStrong1')}</strong>
                {t('homepage.heroLedePart2')}{' '}
                <strong>{t('homepage.heroLedeStrong2')}</strong>
                {t('homepage.heroLedePart3')}
              </p>
              <p className="hero-roi">{t('homepage.heroRoiLine')}</p>
              <div className="actions">
                <a className="btn-primary" href="/login">
                  {t('homepage.heroCtaStartFree')}
                </a>
                <a className="btn-ghost" href="#mechanic">
                  {t('homepage.heroCtaReadMechanic')}
                </a>
              </div>
              <div className="stack">
                <span>{t('homepage.heroStack1')}</span>
                <span>{t('homepage.heroStack2')}</span>
                <span>{t('homepage.heroStack3')}</span>
              </div>
            </div>

            {/* Interactive stale-propagation mini demo */}
            <div className="stale-demo" aria-label={t('homepage.demoAriaLabel')}>
              <div className="sd-head">
                <div className="dotrow">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span
                  className="mono"
                  style={{ fontSize: 10, letterSpacing: '.14em', color: 'var(--ink-3)' }}
                >
                  PROJECT · game-design-bible
                </span>
              </div>
              <div className="sd-body">
                <div className={`dcard kind-decision${flash ? ' flash' : ''}`}>
                  <div className="dc-head">
                    <span className="dc-kind">▸ DECISION</span>
                    <span className="dc-id">c-04</span>
                    <span className="dc-tail">{t('homepage.demoByYou')}</span>
                  </div>
                  <div className="dc-body">
                    {decisionText === 'original' ? (
                      <>{t('homepage.demoCardDecision', { value: ORIGINAL_DECISION })}</>
                    ) : (
                      <>
                        {t('homepage.demoCardDecisionPrefix')}{' '}
                        <span style={{ background: 'var(--amber-soft)', padding: '0 3px' }}>
                          {NEW_DECISION}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className={`dcard kind-statement${staleDeps.has('dep1') ? ' stale' : ''}`}>
                  <div className="dc-head">
                    <span className="dc-kind">● STATEMENT</span>
                    <span className="dc-id">c-11</span>
                    <span className="dc-tail">→ depends-on c-04</span>
                  </div>
                  <div className="dc-body">
                    {t('homepage.demoCardStatement1')}
                  </div>
                </div>
                <div className={`dcard kind-statement${staleDeps.has('dep2') ? ' stale' : ''}`}>
                  <div className="dc-head">
                    <span className="dc-kind">● STATEMENT</span>
                    <span className="dc-id">c-17</span>
                    <span className="dc-tail">→ depends-on c-04</span>
                  </div>
                  <div className="dc-body">
                    {t('homepage.demoCardStatement2')}
                  </div>
                </div>
                <div className={`dcard kind-question${staleDeps.has('dep3') ? ' stale' : ''}`}>
                  <div className="dc-head">
                    <span className="dc-kind">? QUESTION</span>
                    <span className="dc-id">c-22</span>
                    <span className="dc-tail">→ depends-on c-04</span>
                  </div>
                  <div className="dc-body">
                    {t('homepage.demoCardQuestion')}
                  </div>
                </div>
              </div>
              <div className="demo-cta">
                <div className="narrate mono">{narrate}</div>
                <button onClick={runDemo} disabled={staleState === 'rewriting'}>
                  {demoButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Problem ── */}
        <section id="problem" className="problem">
          <div className="head">
            <h2 className="serif">
              {t('homepage.problemHeading')}{' '}
              <em>{t('homepage.problemHeadingEm')}</em>
            </h2>
          </div>
          <div className="problem-grid">
            <div className="problem-col">
              <span className="num">{t('homepage.problem1Num')}</span>
              <h3 className="serif">{t('homepage.problem1Title')}</h3>
              <p>{t('homepage.problem1Body')}</p>
            </div>
            <div className="problem-col">
              <span className="num">{t('homepage.problem2Num')}</span>
              <h3 className="serif">{t('homepage.problem2Title')}</h3>
              <p>{t('homepage.problem2Body')}</p>
            </div>
            <div className="problem-col">
              <span className="num">{t('homepage.problem3Num')}</span>
              <h3 className="serif">{t('homepage.problem3Title')}</h3>
              <p>{t('homepage.problem3Body')}</p>
            </div>
          </div>
        </section>

        {/* ── Pull quote ── */}
        <section className="pullquote">
          <blockquote className="serif">
            {t('homepage.pullQuote')}
          </blockquote>
          <cite>{t('homepage.pullQuoteCite')}</cite>
        </section>

        {/* ── Mechanic ── */}
        <section id="mechanic" className="mechanic">
          <div className="secthead">
            <span className="lbl">{t('homepage.mechanicLabel')}</span>
            <h2 className="serif">
              {t('homepage.mechanicHeadingLine1')}
              <br />
              <em>{t('homepage.mechanicHeadingLine2')}</em>
            </h2>
          </div>

          <div className="mech-grid">
            <div className="beats">
              <div className="beat">
                <div className="roman">I.</div>
                <div>
                  <h3 className="serif">{t('homepage.mechanic1Title')}</h3>
                  <p>
                    {t('homepage.mechanic1BodyPart1')}{' '}
                    <span className="code">statement, question, observation, decision, note, gap</span>.
                    {t('homepage.mechanic1BodyPart2')}
                  </p>
                </div>
              </div>
              <div className="beat">
                <div className="roman">II.</div>
                <div>
                  <h3 className="serif">{t('homepage.mechanic2Title')}</h3>
                  <p>
                    {t('homepage.mechanic2BodyPart1')} <span className="code">decision</span>{' '}
                    {t('homepage.mechanic2BodyPart2')}{' '}
                    <span className="code">→depends-on</span>{' '}
                    {t('homepage.mechanic2BodyPart3')}{' '}
                    <span className="code">stale: true</span>.{' '}
                    {t('homepage.mechanic2BodyPart4')}
                  </p>
                </div>
              </div>
              <div className="beat">
                <div className="roman">III.</div>
                <div>
                  <h3 className="serif">{t('homepage.mechanic3Title')}</h3>
                  <p>
                    {t('homepage.mechanic3BodyPart1')} <span className="code">propose_card</span>{' '}
                    {t('homepage.mechanic3BodyPart2')}{' '}
                    <span className="code">kind=gap</span>.{' '}
                    {t('homepage.mechanic3BodyPart3')}
                  </p>
                </div>
              </div>
              <div className="beat">
                <div className="roman">IV.</div>
                <div>
                  <h3 className="serif">{t('homepage.mechanic4Title')}</h3>
                  <p>
                    {t('homepage.mechanic4BodyPart1')}{' '}
                    <span className="code">gap_kind=conflict</span>.
                  </p>
                </div>
              </div>
            </div>

            <div className="mockwrap">
              <div className="mock">
                <div className="mock-head">
                  <span className="mh-brand">Draftlize</span>
                  <span className="mh-tag">DOGFOOD · v0.9</span>
                  <span className="mh-path">/coffee-saas/threads/pricing</span>
                </div>
                <div className="mock-row">
                  <div className="mock-side">
                    <div className="lbl-side">{t('homepage.mockThreads')}</div>
                    <div className="thread active">
                      pricing-strategy<span className="cnt">·12</span>
                    </div>
                    <div className="thread">
                      market-research<span className="cnt">·7</span>
                    </div>
                    <div className="thread">
                      competitor-deep<span className="cnt">·9</span>
                    </div>
                    <div className="thread" style={{ color: 'var(--ink-3)' }}>
                      intent-v1<span className="cnt">·4</span>
                    </div>
                  </div>
                  <div className="mock-main">
                    <div
                      className="lbl-side mono"
                      style={{
                        fontSize: 9,
                        letterSpacing: '.14em',
                        color: 'var(--ink-3)',
                        marginBottom: 10,
                        fontWeight: 700,
                      }}
                    >
                      {t('homepage.mockCards')}
                    </div>
                    <div className="mini-card kind-decision">
                      <div className="mc-h">▸ DECISION · c-04</div>
                      <div>{t('homepage.mockCardDecision')}</div>
                    </div>
                    <div className="mini-card kind-statement">
                      <div className="mc-h">● STATEMENT · c-09 · → depends-on c-04</div>
                      <div>{t('homepage.mockCardStatement1')}</div>
                    </div>
                    <div className="mini-card kind-statement">
                      <div className="mc-h">● STATEMENT · c-12 · → depends-on c-04</div>
                      <div>{t('homepage.mockCardStatement2')}</div>
                    </div>
                    <div className="mini-card kind-gap">
                      <div className="mc-h">○ GAP · c-18 · gap_kind=conflict</div>
                      <div>{t('homepage.mockCardGap')}</div>
                    </div>
                  </div>
                  <div className="mock-aside">
                    <div className="lbl-side">
                      {t('homepage.mockGapLedger')} <span className="badge">3</span>
                    </div>
                    <div
                      className="mini-card kind-gap"
                      style={{ marginBottom: 8, fontSize: 11 }}
                    >
                      <div className="mc-h">○ INTERP · c-15</div>
                      <div>{t('homepage.mockGapInterp1')}</div>
                    </div>
                    <div
                      className="mini-card kind-gap"
                      style={{ marginBottom: 8, fontSize: 11 }}
                    >
                      <div className="mc-h">○ CONFLICT · c-18</div>
                      <div>{t('homepage.mockGapConflict')}</div>
                    </div>
                    <div className="mini-card kind-gap" style={{ fontSize: 11 }}>
                      <div className="mc-h">○ INTERP · c-21</div>
                      <div>{t('homepage.mockGapInterp2')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Founder note (Tier 2 trust) ── */}
        <section className="founder">
          <div className="wrap">
            <div>
              <span className="label">{t('homepage.founderLabel')}</span>
              <h2 className="serif">
                {t('homepage.founderHeading')}
                <br />
                <em>{t('homepage.founderHeadingEm')}</em>
              </h2>
            </div>
            <div>
              <p className="body">{t('homepage.founderBody')}</p>
              <div className="sig">{t('homepage.founderSig')}</div>
            </div>
          </div>
        </section>

        {/* ── Personas (6) ── */}
        <section id="personas" className="personas">
          <div className="head">
            <div>
              <span className="label label-amber">{t('homepage.personasLabel')}</span>
              <h2 className="serif">
                {t('homepage.personasHeadingLine1')}
                <br />
                {t('homepage.personasHeadingLine2Part1')}{' '}
                <em>{t('homepage.personasHeadingLine2Em')}</em>{' '}
                {t('homepage.personasHeadingLine2Part2')}
              </h2>
            </div>
            <p>{t('homepage.personasIntro')}</p>
          </div>

          <div className="persona-grid">
            <article className="persona">
              <div className="pn">{t('homepage.persona1Tag')}</div>
              <h3 className="pname serif">{t('homepage.persona1Name')}</h3>
              <p className="role">{t('homepage.persona1Role')}</p>
              <div className="pain">
                <span className="rl">{t('homepage.painLabel')}</span>
                <p>{t('homepage.persona1Pain')}</p>
              </div>
              <div className="gain">
                <span className="rl">{t('homepage.gainLabel')}</span>
                <p>{t('homepage.persona1Gain')}</p>
              </div>
              <div className="snippet">
                <span className="sl">{t('homepage.persona1SnippetLabel')}</span>
                {t('homepage.persona1Snippet')}
              </div>
            </article>

            <article className="persona">
              <div className="pn">{t('homepage.persona2Tag')}</div>
              <h3 className="pname serif">{t('homepage.persona2Name')}</h3>
              <p className="role">{t('homepage.persona2Role')}</p>
              <div className="pain">
                <span className="rl">{t('homepage.painLabel')}</span>
                <p>{t('homepage.persona2Pain')}</p>
              </div>
              <div className="gain">
                <span className="rl">{t('homepage.gainLabel')}</span>
                <p>
                  {t('homepage.persona2GainPart1')} <span className="kbd">mark-built</span>{' '}
                  {t('homepage.persona2GainPart2')}
                </p>
              </div>
              <div className="snippet">
                <span className="sl">{t('homepage.persona2SnippetLabel')}</span>
                {t('homepage.persona2Snippet')}
              </div>
            </article>

          </div>
        </section>

        {/* ── Surfaces ── */}
        <section id="surfaces" className="surfaces">
          <div className="head">
            <span className="label label-amber">{t('homepage.surfacesLabel')}</span>
            <h2 className="serif">
              {t('homepage.surfacesHeadingPart1')} <em>{t('homepage.surfacesHeadingEm')}</em>
            </h2>
            <p>
              {t('homepage.surfacesIntroPart1')}{' '}
              <span className="kbd">{'draftlize://<slug>/<cid>'}</span>{' '}
              {t('homepage.surfacesIntroPart2')}
            </p>
          </div>

          <div className="surfaces-grid">
            <div className="surface s-web">
              <div className="sh">
                <span className="stitle">{t('homepage.surfaceWebTitle')}</span>
                <span className="stag">draftlize.com</span>
              </div>
              <div className="body">
                <div className="web-card">
                  <div className="wc-h">
                    <span style={{ color: 'var(--ink-0)' }}>▸ DECISION</span>
                    <span className="wc-id">c-04 · pricing-strategy</span>
                  </div>
                  <div className="wc-body">{t('homepage.surfaceWebCardBody')}</div>
                  <div className="wc-meta">{t('homepage.surfaceWebCardMeta')}</div>
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10.5,
                    letterSpacing: '.08em',
                    color: 'var(--ink-3)',
                    marginTop: 16,
                  }}
                >
                  {t('homepage.surfaceWebCaption')}
                </div>
              </div>
            </div>

            <div className="surface s-claude">
              <div className="sh">
                <span className="stitle">{t('homepage.surfaceClaudeTitle')}</span>
                <span className="stag">/draftlize:cite c-04</span>
              </div>
              <div className="body">
                <div className="cc-msg user">
                  <span className="who">{t('homepage.surfaceClaudeWhoYou')}</span>
                  <span className="slash">/draftlize:cite</span> c-04 —{' '}
                  {t('homepage.surfaceClaudeUserMsg')}
                </div>
                <div className="cc-msg">
                  <span className="who">CLAUDE</span>
                  {t('homepage.surfaceClaudeReplyPrefix')}{' '}
                  <code>.draftlize/cards/c-04.md</code>:
                  <div className="cited">
                    ▸ <strong>DECISION · c-04</strong>
                    <br />
                    {t('homepage.surfaceClaudeCitedBody')}
                    <br />
                    <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>
                      → supports c-09, c-12 · status: built
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ── Documents ── */}
        <section id="docs" className="docs">
          <div className="head">
            <div className="l">
              <span className="label label-amber">{t('homepage.docsLabel')}</span>
              <h2 className="serif">
                {t('homepage.docsHeadingLine1')}
                <br />
                <em>{t('homepage.docsHeadingLine2')}</em>
              </h2>
            </div>
            <div className="r">
              <p>
                {t('homepage.docsIntroPart1')} <em>{t('homepage.docsIntroEm')}</em>
                {t('homepage.docsIntroPart2')}{' '}
                <span className="kbd">source_snapshot</span>
                {t('homepage.docsIntroPart3')}{' '}
                <span className="kbd">documents_stale</span>{' '}
                {t('homepage.docsIntroPart4')}
              </p>
            </div>
          </div>
          <div className="docs-grid">
            <article className="doc-card">
              <span className="label">{t('homepage.docTemplate1Label')}</span>
              <h4 className="serif">design-doc</h4>
              <div className="preview">
                <div className="hdg">{t('homepage.doc1Hdg1')}</div>
                <span className="mc">c-04</span>{t('homepage.doc1Row1')}
                <br />
                <div className="hdg">{t('homepage.doc1Hdg2')}</div>
                <span className="mc">c-12</span>{t('homepage.doc1Row2')}
                <br />
                <div className="hdg">{t('homepage.doc1Hdg3')}</div>
                <div className="bul">{t('homepage.doc1Bul1')}</div>
                <div className="bul">{t('homepage.doc1Bul2')}</div>
                <div className="hdg">{t('homepage.doc1Hdg4')}</div>
                <span className="mc">c-21</span>{t('homepage.doc1Row3')}
              </div>
              <div className="foot">
                <span>{t('homepage.doc1Cards')}</span>
                <span className="stale-dot">{t('homepage.doc1Stale')}</span>
              </div>
            </article>
            <article className="doc-card">
              <span className="label">{t('homepage.docTemplate2Label')}</span>
              <h4 className="serif">pitch-deck</h4>
              <div className="preview">
                <div className="hdg">{t('homepage.doc2Hdg1')}</div>
                <span className="mc">c-02</span>{t('homepage.doc2Row1')}
                <br />
                <div className="hdg">{t('homepage.doc2Hdg2')}</div>
                <span className="mc">c-03</span>{t('homepage.doc2Row2')}
                <br />
                <div className="hdg">{t('homepage.doc2Hdg3')}</div>
                <span className="mc">c-05</span>{t('homepage.doc2Row3')}
                <br />
                <div className="hdg">{t('homepage.doc2Hdg4')}</div>
                <span className="mc">c-08</span>{t('homepage.doc2Row4')}
                <br />
                <div className="hdg">{t('homepage.doc2Hdg5')}</div>
                <span className="mc">c-12</span>{t('homepage.doc2Row5')}
              </div>
              <div className="foot">
                <span>{t('homepage.doc2Cards')}</span>
                <span style={{ color: 'var(--green)' }}>{t('homepage.docClean')}</span>
              </div>
            </article>
            <article className="doc-card">
              <span className="label">{t('homepage.docTemplate3Label')}</span>
              <h4 className="serif">blog-post</h4>
              <div className="preview">
                <div className="hdg">{t('homepage.doc3Hdg1')}</div>
                {t('homepage.doc3Body1Part1')}{' '}
                <em style={{ color: 'var(--ink-2)' }}>{t('homepage.doc3Body1Em')}</em>{' '}
                {t('homepage.doc3Body1Part2')}
                <div className="hdg">{t('homepage.doc3Hdg2')}</div>
                {t('homepage.doc3Body2Part1')} <em>{t('homepage.doc3Body2Em')}</em>{' '}
                {t('homepage.doc3Body2Part2')}
              </div>
              <div className="foot">
                <span>{t('homepage.doc3Cards')}</span>
                <span className="stale-dot">{t('homepage.doc3Stale')}</span>
              </div>
            </article>
            <article className="doc-card">
              <span className="label">{t('homepage.docTemplate4Label')}</span>
              <h4 className="serif">outline</h4>
              <div className="preview">
                <div className="bul">{t('homepage.doc4Bul1')}</div>
                <div className="bul">{t('homepage.doc4Bul2')}</div>
                <div className="bul" style={{ paddingLeft: 24 }}>
                  {t('homepage.doc4Bul3')}
                </div>
                <div className="bul" style={{ paddingLeft: 24 }}>
                  {t('homepage.doc4Bul4')}
                </div>
                <div className="bul">{t('homepage.doc4Bul5')}</div>
                <div className="bul">{t('homepage.doc4Bul6')}</div>
                <div className="bul">{t('homepage.doc4Bul7')}</div>
              </div>
              <div className="foot">
                <span>{t('homepage.doc4Cards')}</span>
                <span style={{ color: 'var(--green)' }}>{t('homepage.docClean')}</span>
              </div>
            </article>
          </div>
        </section>

        {/* ── Claude Code (inverted) ── */}
        <section id="claude-code" className="cc">
          <div className="head">
            <div>
              <span className="label">{t('homepage.ccLabel')}</span>
              <h2 className="serif" style={{ marginTop: 16 }}>
                {t('homepage.ccHeadingLine1')}
                <br />
                <em>{t('homepage.ccHeadingLine2')}</em>
              </h2>
            </div>
            <div>
              <p>
                {t('homepage.ccIntroPart1')} <strong>{t('homepage.ccIntroStrong1')}</strong>{' '}
                {t('homepage.ccIntroPart2')}{' '}
                <span className="kbd">draftlize-mcp</span>{' '}
                {t('homepage.ccIntroPart3')} <strong>{t('homepage.ccIntroStrong2')}</strong>{' '}
                {t('homepage.ccIntroPart4')}
              </p>
            </div>
          </div>

          <div className="cc-grid">
            <div className="cc-term">
              <div className="term-head">
                <div className="dotrow">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="term-title">~/code/coffee-saas — zsh</span>
              </div>
              <div className="term-body">
                <div className="ln">
                  <span className="ln-n">1</span>
                  <span>
                    <span className="cmt">$</span> <span className="cmd">draftlize</span>{' '}
                    <span className="arg">link coffee-saas-abc123</span>
                  </span>
                </div>
                <div className="ln">
                  <span className="ln-n">2</span>
                  <span className="ok">
                    ✓ linked · 4 slash commands installed at .claude/skills/draftlize/
                  </span>
                </div>
                <div className="ln">
                  <span className="ln-n">3</span>
                  <span>
                    <span className="cmt">$</span> <span className="cmd">draftlize</span>{' '}
                    <span className="arg">pull</span>
                  </span>
                </div>
                <div className="ln">
                  <span className="ln-n">4</span>
                  <span className="ok">✓ 47 cards → .draftlize/cards/</span>
                </div>
                <div className="ln">
                  <span className="ln-n">5</span>
                  <span className="cmt">&nbsp;</span>
                </div>
                <div className="ln">
                  <span className="ln-n">6</span>
                  <span className="cmt"># inside Claude Code, the agent now resolves:</span>
                </div>
                <div className="ln">
                  <span className="ln-n">7</span>
                  <span>
                    <span className="sl">/draftlize:cite</span> <span className="arg">c-7</span>
                  </span>
                </div>
                <div className="ln">
                  <span className="ln-n">8</span>
                  <span className="cmt">  reads .draftlize/cards/c-7.md verbatim into chat</span>
                </div>
                <div className="ln">
                  <span className="ln-n">9</span>
                  <span>
                    <span className="sl">/draftlize:flag</span>{' '}
                    <span className="arg">c-7 "Stripe webhook timing breaks polling fallback"</span>
                  </span>
                </div>
                <div className="ln">
                  <span className="ln-n">10</span>
                  <span className="cmt">  POSTs a gap card to the Draftlize GapLedger</span>
                </div>
                <div className="ln">
                  <span className="ln-n">11</span>
                  <span className="cmt">&nbsp;</span>
                </div>
                <div className="ln">
                  <span className="ln-n">12</span>
                  <span>
                    <span className="cmt">$</span> <span className="cmd">draftlize</span>{' '}
                    <span className="arg">mark-built c-12</span>
                  </span>
                </div>
                <div className="ln">
                  <span className="ln-n">13</span>
                  <span className="ok">✓ c-12 implementation_status: built</span>
                </div>
                <div className="ln">
                  <span className="ln-n">14</span>
                  <span className="cmt">
                    {'  '}edit c-12 later — status auto-flips to drift_detected
                  </span>
                </div>
              </div>
            </div>

            <div className="cc-side">
              <div className="cc-bullet">
                <div className="label">{t('homepage.ccBullet1Label')}</div>
                <h5 className="serif">{t('homepage.ccBullet1Title')}</h5>
                <p>
                  <span className="kbd">read_card</span> · <span className="kbd">list_cards</span>{' '}
                  · <span className="kbd">search_cards</span>{' '}
                  {t('homepage.ccBullet1Body')}
                </p>
              </div>
              <div className="cc-bullet">
                <div className="label">{t('homepage.ccBullet2Label')}</div>
                <h5 className="serif">{t('homepage.ccBullet2Title')}</h5>
                <p>
                  {t('homepage.ccBullet2BodyPart1')}{' '}
                  <span className="kbd">source_agent</span>
                  {t('homepage.ccBullet2BodyPart2')}
                </p>
              </div>
              <div className="cc-bullet">
                <div className="label">{t('homepage.ccBullet3Label')}</div>
                <h5 className="serif">{t('homepage.ccBullet3Title')}</h5>
                <p>
                  {t('homepage.ccBullet3BodyPart1')} <span className="kbd">built</span>
                  {t('homepage.ccBullet3BodyPart2')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Comparison table (Tier 1 differentiation) ── */}
        <section id="compare" className="compare">
          <div className="head">
            <div>
              <span className="label">{t('homepage.compareLabel')}</span>
              <h2 className="serif">
                {t('homepage.compareHeading')}
                <br />
                <em>{t('homepage.compareHeadingEm')}</em>
              </h2>
            </div>
            <p>{t('homepage.compareIntro')}</p>
          </div>
          <table className="compare-table">
            <thead>
              <tr>
                <th></th>
                <th className="ours">{t('homepage.compareColDraftlize')}</th>
                <th>{t('homepage.compareColNotion')}</th>
                <th>{t('homepage.compareColChatGPT')}</th>
                <th>{t('homepage.compareColLinear')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('homepage.compareRow1')}</td>
                <td className="ours yes">{t('homepage.compareLegendYes')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="partial">{t('homepage.compareLegendPartial')}</td>
              </tr>
              <tr>
                <td>{t('homepage.compareRow2')}</td>
                <td className="ours yes">{t('homepage.compareLegendYes')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
              </tr>
              <tr>
                <td>{t('homepage.compareRow3')}</td>
                <td className="ours yes">{t('homepage.compareLegendYes')}</td>
                <td className="partial">{t('homepage.compareLegendPartial')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="partial">{t('homepage.compareLegendPartial')}</td>
              </tr>
              <tr>
                <td>{t('homepage.compareRow4')}</td>
                <td className="ours yes">{t('homepage.compareLegendYes')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
              </tr>
              <tr>
                <td>{t('homepage.compareRow5')}</td>
                <td className="ours yes">{t('homepage.compareLegendYes')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="partial">{t('homepage.compareLegendPartial')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
              </tr>
              <tr>
                <td>{t('homepage.compareRow6')}</td>
                <td className="ours yes">{t('homepage.compareLegendYes')}</td>
                <td className="partial">{t('homepage.compareLegendPartial')}</td>
                <td className="no">{t('homepage.compareLegendNo')}</td>
                <td className="yes">{t('homepage.compareLegendYes')}</td>
              </tr>
              <tr>
                <td>{t('homepage.compareRow7')}</td>
                <td className="ours yes">{t('homepage.compareLegendYes')}</td>
                <td className="yes">{t('homepage.compareLegendYes')}</td>
                <td className="partial">{t('homepage.compareLegendPartial')}</td>
                <td className="yes">{t('homepage.compareLegendYes')}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* ── Pricing — prepaid wallet (3 selling points, not tiers) ── */}
        <section id="pricing" className="pricing">
          <div className="head">
            <h2 className="serif">
              {t('pricing.pointsHeadingPart1')} <em>{t('pricing.pointsHeadingEm')}</em>
            </h2>
            <div className="softcap">{t('pricing.pointsSubhead')}</div>
          </div>
          <div className="pricing-grid">
            <div className="tier">
              <div className="tname">{t('pricing.point1Title')}</div>
              <div className="price serif">$5</div>
              <div className="quota">{t('pricing.point1Tag')}</div>
              <p style={{ marginTop: 12, color: 'var(--ink-2)' }}>{t('pricing.point1Body')}</p>
            </div>
            <div className="tier">
              <div className="tname">{t('pricing.point2Title')}</div>
              <div className="quota">{t('pricing.point2Tag')}</div>
              <p style={{ marginTop: 12, color: 'var(--ink-2)' }}>{t('pricing.point2Body')}</p>
            </div>
            <div className="tier">
              <div className="tname">{t('pricing.point3Title')}</div>
              <div className="quota">{t('pricing.point3Tag')}</div>
              <p style={{ marginTop: 12, color: 'var(--ink-2)' }}>{t('pricing.point3Body')}</p>
            </div>
          </div>
          <div className="actions" style={{ marginTop: 32, justifyContent: 'center', display: 'flex' }}>
            <a className="btn-primary" href="/">{t('pricing.prepaidHeroCta')}</a>
          </div>
        </section>

        {/* ── Voices + FAQ ── */}
        <section className="voices">
          <div className="head">
            <h2 className="serif">
              {t('homepage.voicesHeadingPart1')} <em>{t('homepage.voicesHeadingEm')}</em>
            </h2>
          </div>
          <div className="voices-grid">
            <div className="testimonials">
              <div className="tcard">
                <blockquote>{t('homepage.testimonial1Quote')}</blockquote>
                <div className="cite">
                  <span className="who">Yuki R.</span>
                  <span className="role">{t('homepage.testimonial1Role')}</span>
                </div>
              </div>
              <div className="tcard">
                <blockquote>{t('homepage.testimonial2Quote')}</blockquote>
                <div className="cite">
                  <span className="who">Lena H.</span>
                  <span className="role">{t('homepage.testimonial2Role')}</span>
                </div>
              </div>
              <div className="tcard">
                <blockquote>{t('homepage.testimonial3Quote')}</blockquote>
                <div className="cite">
                  <span className="who">Sam K.</span>
                  <span className="role">{t('homepage.testimonial3Role')}</span>
                </div>
              </div>
            </div>

            <div className="faq">
              <h3>{t('homepage.faqHeading')}</h3>
              <details>
                <summary>{t('homepage.faq1Question')}</summary>
                <p>
                  {t('homepage.faq1AnswerPart1')}{' '}
                  <strong>{t('homepage.faq1AnswerPart2')}</strong>
                </p>
              </details>
              <details>
                <summary>{t('homepage.faq2Question')}</summary>
                <p>{t('homepage.faq2Answer')}</p>
              </details>
              <details>
                <summary>{t('homepage.faq3Question')}</summary>
                <p>{t('homepage.faq3Answer')}</p>
              </details>
              <details>
                <summary>{t('homepage.faq4Question')}</summary>
                <p>{t('homepage.faq4Answer')}</p>
              </details>
              <details>
                <summary>{t('homepage.faq5Question')}</summary>
                <p>{t('homepage.faq5Answer')}</p>
              </details>
            </div>
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="final">
          <h2 className="serif">
            {t('homepage.finalHeadingLine1')}
            <br />
            <em>{t('homepage.finalHeadingLine2')}</em>
          </h2>
          <p>{t('homepage.finalBody')}</p>
          <div className="actions">
            <a className="btn-light" href="/login">
              {t('homepage.finalCta')}
            </a>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer>
          <div className="foot-top">
            <div className="brand-block">
              <span className="brand serif">Draftlize</span>
              <p>{t('homepage.footerBlurb')}</p>
            </div>
            <div className="col">
              <h6>{t('homepage.footerSignIn')}</h6>
              <div className="auth-row">
                <a href="/api/auth/github/start" className="auth" aria-label={t('homepage.footerAriaGitHub')}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                  </svg>
                  {t('homepage.footerGitHub')}
                </a>
                <a href="/api/auth/google/start" className="auth" aria-label={t('homepage.footerAriaGoogle')}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 2a10 10 0 0 1 0 20"></path>
                    <path d="M2 12h20"></path>
                  </svg>
                  {t('homepage.footerGoogle')}
                </a>
                <a href="/login" className="auth" aria-label={t('homepage.footerAriaMagicLink')}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="4" width="20" height="16"></rect>
                    <path d="m22 7-10 5L2 7"></path>
                  </svg>
                  {t('homepage.footerMagicLink')}
                </a>
              </div>
            </div>
            <div className="col">
              <h6>{t('homepage.footerLanguage')}</h6>
              <p className="lang">
                EN · 中文 <em>{t('homepage.footerLanguageNote')}</em>
              </p>
            </div>
          </div>
          <div className="foot-bot">
            <span>{t('homepage.footerEdition')}</span>
            <span className="nav-min">
              <a href="#">{t('homepage.footerPrivacy')}</a>
              <a href="#">{t('homepage.footerTerms')}</a>
              <a href="mailto:hi@draftlize.com">{t('homepage.footerContact')}</a>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
